import { useCallback, useMemo } from "react";
import classnames from "classnames";

import type { ParameterTimeSeriesConfiguration, Widget } from "@/lib/api";
import {
  normalizeTimeSeriesWidgetFormValues,
  readTimeSeriesConfiguration,
  TIME_SERIES_WIDGET_INPUTS,
} from "@/lib/api/time-series-widget-configuration.utils";
import { WidgetDialogProps } from "@/features/widget-forms/dialogs/widget-dialog-types";
import { FormValues } from "@/types/widget";
import Dialog from "@/components/dialog/dialog";
import { MapWidgetDndProvider } from "@/features/widget-forms/forms/map-widget-editor/map-widget-dnd-provider";
import { TimeSeriesConfiguratorBody } from "@/features/widget-forms/forms/weather-model-map/components/time-series-configurator-body";
import {
  createEmptyTimeSeriesConfiguratorDraft,
  toTimeSeriesConfiguratorDraft,
  type TimeSeriesConfiguratorDraft,
} from "@/features/widget-forms/forms/weather-model-map/time-series-configurator.utils";
import { useAllTimeSeriesParametersQuery } from "@/features/shared/widgets/time-series-widget/use-all-time-series-parameters-query";
import { usePropertyKindsQuery } from "@/lib/parameters/use-property-kinds-query";
import { WidgetContextHost } from "@/features/shared/widget-context/widget-context-host";
import { useAdminDashboard } from "@/features/admin/hooks";
import { useTranslation } from "@/lib/i18n";
import {
  TimeSeriesChartPreviewBody,
  TimeSeriesChartPreviewProvider,
  TimeSeriesChartPreviewToolbar,
} from "@/features/widget-forms/forms/observations-map/observations-map-time-series-chart-preview";
import configuratorModalStyles from "@/features/widget-forms/forms/weather-model-map/modals/modal-styles.module.css";
import dialogStyles from "@/features/widget-forms/forms/weather-model-map/weather-model-map-dialog.module.css";

function initialConfiguratorConfig(
  initialValues: Partial<Widget>,
): ParameterTimeSeriesConfiguration | TimeSeriesConfiguratorDraft {
  const existing = readTimeSeriesConfiguration(initialValues.configuration);
  // Create starts with an empty title even though CreateWidgetDialog prefills
  // Widget.title with "Naamloos". The header field is required and autofocused;
  // copying that default would show as a filled name.
  return existing
    ? toTimeSeriesConfiguratorDraft(existing)
    : createEmptyTimeSeriesConfiguratorDraft();
}

/**
 * Top-level CMS dialog for the `time_series` widget. The shared configurator
 * body is the editor (not a nested modal on a map widget).
 */
export function TimeSeriesWidgetDialog({
  initialValues,
  open,
  onOpenChange,
  onSubmit,
  onReset,
}: WidgetDialogProps) {
  const { t } = useTranslation();
  const dashboard = useAdminDashboard(false);
  const parametersQuery = useAllTimeSeriesParametersQuery(open);
  const parameters = parametersQuery.data ?? [];
  const { unitFamilies } = usePropertyKindsQuery(open);

  const existingConfig = readTimeSeriesConfiguration(initialValues.configuration);
  const initialConfig = useMemo(
    () => initialConfiguratorConfig(initialValues as Partial<Widget>),
    [initialValues],
  );

  const handleDone = useCallback(
    (config: ParameterTimeSeriesConfiguration) => {
      return onSubmit(
        normalizeTimeSeriesWidgetFormValues({
          ...initialValues,
          title: config.title,
          configuration: {
            type: "time_series",
            inputs: TIME_SERIES_WIDGET_INPUTS,
            parameter_time_series_configuration: config,
          },
        }) as FormValues,
      );
    },
    [initialValues, onSubmit],
  );

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      onReset();
    }
    onOpenChange(nextOpen);
  };

  return (
    <MapWidgetDndProvider>
      <Dialog open={open} onOpenChange={handleOpenChange} onClose={onReset}>
        <Dialog.Content
          className={classnames(
            configuratorModalStyles.configuratorMedium,
            configuratorModalStyles.configuratorDialog,
            dialogStyles.dialog,
          )}
        >
          {open && (
            <TimeSeriesConfiguratorBody
              key={existingConfig ? "edit" : "create"}
              initialConfig={initialConfig}
              parameters={parameters}
              unitFamilies={unitFamilies}
              datasetLabels={parametersQuery.datasetLabels}
              timeSeriesConfigurations={existingConfig ? [existingConfig] : []}
              configuratorIndex={existingConfig ? 0 : null}
              parametersLoading={parametersQuery.isPending || parametersQuery.isError}
              editableTitle
              doneLabel={t("general.save")}
              className={configuratorModalStyles.configuratorBodyFill}
              onDone={handleDone}
              onCancel={onReset}
              renderPreview={(liveConfig) => ({
                wrap: (node) => (
                  <WidgetContextHost dashboardId={dashboard?.id}>
                    <TimeSeriesChartPreviewProvider
                      config={liveConfig}
                      parameters={parameters}
                      parameterModelIndex={parametersQuery.parameterModelIndex}
                    >
                      {node}
                    </TimeSeriesChartPreviewProvider>
                  </WidgetContextHost>
                ),
                toolbar: <TimeSeriesChartPreviewToolbar />,
                content: <TimeSeriesChartPreviewBody />,
              })}
            />
          )}
        </Dialog.Content>
      </Dialog>
    </MapWidgetDndProvider>
  );
}
